import { createAsyncThunk } from '@reduxjs/toolkit';
import { checkModule } from './helpers';
import { abortManager } from './abortController';
import { queryQueue } from './queryQueue';
import { type CdeebeeState, type CdeebeeRequestOptions } from './types';

export const request = createAsyncThunk(
  'cdeebee/request',
  async (options: CdeebeeRequestOptions<unknown>, { rejectWithValue, getState, requestId, signal }) => {
    const startedAt = new Date().toUTCString();
    const { cdeebee: { settings } } = getState() as { cdeebee: CdeebeeState<unknown> };

    const abort = abortManager(signal, options.api, requestId);
    const withCallback = options.onResult && typeof options.onResult === 'function';

    checkModule(settings, 'cancelation', abort.init);

    const executeRequest = async () => {
      try {
        const { method = 'POST', body, headers = {} } = options;
        const baseHeaders = typeof settings.mergeWithHeaders === 'function'
          ? settings.mergeWithHeaders()
          : (settings.mergeWithHeaders ?? {});

        const extraHeaders: Record<string, string> = { ...baseHeaders, ...headers };

        const baseData = typeof settings.mergeWithData === 'function'
          ? settings.mergeWithData()
          : (settings.mergeWithData ?? {});

        const b = { ...baseData, ...(body ?? {}) };
        let requestData: FormData | string = JSON.stringify(b);
        const isFormData = !!options.files;

        // handling files
        if (options.files) {
          const formData = new FormData();
          const fileKey = options.fileKey || settings.fileKey;
          const bodyKey = options.bodyKey || settings.bodyKey;

          for (let i = 0; i < options.files.length; i += 1) {
            if (fileKey) {
              formData.append(fileKey, options.files[i]);
            }
          }

          if (bodyKey) {
            formData.append(bodyKey, requestData);
          }
          requestData = formData;
        }
        // [end] handling files

        const fetchHeaders: Record<string, string> = {
          'ui-request-id': requestId,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          ...extraHeaders,
        };

        const isGet = method === 'GET';

        const response = await fetch(options.api, {
          method,
          headers: fetchHeaders,
          signal: abort.controller.signal,
          ...(isGet ? {} : { body: requestData }),
        });

        checkModule(settings, 'cancelation', abort.drop);

        let result: unknown;
        const responseType = options.responseType || 'json';

        if (responseType === 'text') {
          result = await response.text();
        } else if (responseType === 'blob') {
          result = await response.blob();
        } else {
          // default: json
          result = await response.json();
        }

        if (!response.ok) {
          if (withCallback) options.onResult!(result);
          return rejectWithValue({ status: response.status, statusText: response.statusText, data: result });
        }

        if (withCallback) options.onResult!(result);
        return { result, startedAt, endedAt: new Date().toUTCString() };
      } catch (error) {
        checkModule(settings, 'cancelation', abort.drop);

        if (withCallback) options.onResult!(error);

        if (error instanceof Error && error.name === 'AbortError') {
          return rejectWithValue({ message: 'Request was cancelled', cancelled: true });
        }

        return rejectWithValue({ message: error instanceof Error ? error.message : 'Unknown error occurred' });
      }
    };

    if (settings.modules.includes('queryQueue')) {
      return queryQueue.enqueue(executeRequest);
    }

    return executeRequest();
  },
);

